import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { useLanguageContext, API_BASE_URL, SCOPES } from '../../_shared';

import { FIELD_TYPES, FORM_TYPES } from '../constants';
import {
	convertDataToFormEntry,
	convertFormEntryToData,
	validateFormField,
} from '../utils';

const axiosClient = axios.create({
	withCredentials: true,
});

/**
 * Handles loading and editing the profile form for a user.
 *
 * @param {Boolean}  isSelf          True, if the current user is viewing their own profile.
 * @param {Object}   displayUser     The user to display.
 * @param {String}   organizationId  The organization ID.
 * @param {Function} setSocialMedia  Updates user profile with the user's social media info.
 *
 * @returns {Object} profile  State related to the profile form.
 *
 * @returns {Object} profile.form               The profile form.
 * @returns {Object} profile.entry              The profile form entry for the user to display.
 * @returns {Object} profile.data               The input data for the user to display.
 * @returns {Object} profile.errors             The input errors (when editing).
 * @returns {Boolean} profile.isEditing         True, if editing the entry.
 * @returns {Boolean} profile.isLoaded          True, if the profile form entry has been loaded.
 * @returns {Boolean} profile.showErrorAlert    True, if there was an error while editing.
 * @returns {Boolean} profile.showSuccessAlert  True, if there was success while editing.
 *
 * @returns {Function}  onChange      Handles changes to the profile form for the current user.
 * @returns {Function}  onSave        Save changes to the profile form for the current user.
 * @returns {Function}  onToggleEdit  Toggles editing state and resets the form entry for the user.
 */
const useProfileForm = (isSelf, displayUser, organizationId, setSocialMedia) => {

	// Keep track of all state related to the profile form
	const [profile, setProfile] = useState({
		form: null,
		entry: null,
		data: {}, // Keep track of the values of each profile form field
		errors: {},
		isEditing: false,
		isLoaded: false, // True, once the form and entry are loaded
		showErrorAlert: false, // True, if API error after saving changes
		showSuccessAlert: false, // True, if API success after saving changes
	});

	const { defaultLanguage, preferredLanguage } = useLanguageContext();

	// Fetch the profile form for the organization
	useEffect(() => {

		axiosClient.get(API_BASE_URL.FORMS, {
			params: {
				type: FORM_TYPES.PROFILE,
				language: preferredLanguage,
				scope: SCOPES.ORGANIZATION,
				scope_id: organizationId,
			}
		}).then((response) => {

			if (response?.data?.length === 1) {
				setProfile((prev) => ({ ...prev, form: response.data[0] }));
			}

		}).catch((error) => {

			console.error(error);
			// TODO: Show error in UI

		});

	}, [organizationId, preferredLanguage]);

	// Fetch the form entry for the user to display
	useEffect(() => {

		if (profile.form && displayUser) {

			axiosClient.get(API_BASE_URL.FORM_ENTRIES, {
				params: {
					user_id: displayUser.id,
					form_id: profile.form.id,
					scope: profile.form.scope,
					scope_id: profile.form.scope_id,
				}
			}).then((response) => {

				if (response?.data?.length === 1) {

					setProfile((prev) => {

						return {
							...prev,
							entry: response.data[0],
							data: convertFormEntryToData(prev.form, response.data[0], { prefill: false }),
							isLoaded: true,
						};

					});

				} else {

					setProfile((prev) => ({ ...prev, isLoaded: true }));

				}

			}).catch((error) => {

				console.error(error);
				// TODO: Show error in UI

			});

		}

	}, [profile.form, displayUser]);

	// Handle changes to the profile form for the current user
	const onChange = useCallback((fieldID, value) => {

		setProfile((prev) => {

			const formField = prev.form.form_fields.find((field) => field.id === fieldID);

			return {
				...prev,
				data: { ...prev.data, [fieldID]: value },
				errors: { ...prev.errors, ...validateFormField(formField, value) },
			};

		});

	}, []);

	// Handle changes to the profile form for the current user
	const onSave = useCallback((form, entry, data) => {

		const [formEntry, errors] = convertDataToFormEntry(form, entry, data, {
			preferredLanguage,
			defaultLanguage,
		});

		formEntry.user_id = displayUser.id;

		// Check if the profile form entry contains any errors
		const hasErrors = form.form_fields?.reduce((acc, field) => {
			return acc || errors[field.id];
		}, false);

		setProfile((prev) => ({
			...prev,
			showErrorAlert: false,
			showSuccessAlert: false,
		}));

		// If there are errors, then don't save it!
		if (hasErrors) {

			setProfile((prev) => ({ ...prev, errors }));
			return;

		}

		let promise;

		// Check if we need to update an existing profile form entry for the current user
		if (formEntry.id) {

			promise = axiosClient.patch(`${API_BASE_URL.FORM_ENTRIES}/${formEntry.id}`, formEntry);

		// Otherwise, create a new one!
		} else {

			promise = axiosClient.post(API_BASE_URL.FORM_ENTRIES, formEntry);

		}

		promise.then((response) => {

			setProfile((prev) => ({
				...prev,
				entry: response.data,
				errors: {},
				isEditing: false,
				isLoaded: true,
				showErrorAlert: false,
				showSuccessAlert: true,
			}));

		}).catch((error) => {

			setProfile((prev) => ({
				...prev,
				errors: {
					apiError: error,
				},
				showErrorAlert: true,
				showSuccessAlert: false,
			}));

		});

	}, [displayUser.id, defaultLanguage, preferredLanguage]);

	const onToggleEdit = useCallback(() => {

		// Toggle ON/OFF the editing state and reset the profile form entry for the user
		setProfile((prev) => ({
			...prev,
			data: convertFormEntryToData(prev.form, prev.entry, { prefill: !prev.isEditing }),
			errors: {},
			showErrorAlert: false,
			showSuccessAlert: false,
			isEditing: !prev.isEditing
		}));

	}, []);

	// Parse the social media information to use for the user profile
	useEffect(() => {

		const updatedData = convertFormEntryToData(
			profile.form,
			profile.entry,
			{ prefill: false },
		);

		setSocialMedia(parseSocialMediaFields(profile.form, updatedData));

	}, [profile.form, profile.entry, setSocialMedia]);

	return {
		profile,
		onChange,
		onSave,
		onToggleEdit,
	};

};

export default useProfileForm;

/**
 * Parses the profile form and form entry to build the list of social media information.
 *
 * @param {object} form  The profile form.
 * @param {object} data  The profile form entry data, keyed by "fieldID".
 *
 * @returns {object} result  The updated list of social media information.
 */
function parseSocialMediaFields(form, data) {

	const result = {};

	if (form?.form_fields) {

		// Loop through the form fields and initialize the social media information
		for (let i = 0, len = form.form_fields.length; i < len; i++) {

			const field = form.form_fields[i];

			// Initialize the social media information for the form field
			if (field.type === FIELD_TYPES.SOCIAL_MEDIA) {

				result[field.subtype] = data[field.id] || null;

			}

		}

	}

	return result;

}
